/**
 * Etsy Data Service
 * Handles fetching Etsy data and storing it in the database
 */

import dimensionExtractor from './dimensionExtractor.js';
import loadingService from './loadingService.js';
import dataProtectionService from './dataProtectionService.js';
import { TIMING } from '../config/constants.js';

import logger from '../utils/logger.js';
class EtsyDataService {
  constructor() {
    this.baseUrl = '/.netlify/functions';

    // Field Classifications for Data Protection
    this.FIELD_CLASSIFICATIONS = {
      local_only: [
        'svgPath',
        'svgPaths',
        'svgTitles',
        'dimensions',
        'publicVisible',
        'isSet',
        'displayOrder',
        'packType',
        'isPack',
        'sizeVariations',
        'colorVariations', // Scraped data - preserve!
        'variation_scraping_status',
        'variation_scraping_error',
        'variation_scraping_last_attempt',
      ],
      etsy_only: [
        'title',
        'description',
        'etsyUrl',
        'etsyShareUrl',
        'state',
        'views',
        'num_favorers',
        'listing_data',
        'inventory_data',
        'quantity',
      ],
      manual_override: [
        'category', // Only update if currently null
      ],
      computed: [
        'available_sizes',
        'available_colors',
        'first_image_url',
        'largest_size_image_url',
        'all_image_urls',
        'inventory_matrix',
        'variation_images_data',
      ],
    };
  }

  /**
   * Filter data fields based on classification to protect local data
   */
  filterSafeFields(updateData, classification = 'etsy_only') {
    const allowedFields = this.FIELD_CLASSIFICATIONS[classification];
    if (!allowedFields) {
      logger.warn(`⚠️ Unknown field classification: ${classification}`);
      return {};
    }

    return Object.keys(updateData)
      .filter((key) => allowedFields.includes(key))
      .reduce((obj, key) => {
        obj[key] = updateData[key];
        return obj;
      }, {});
  }

  /**
   * Create backup of current local data before update
   */
  async backupLocalData(listingId) {
    try {
      logger.log(`💾 Creating backup for listing ${listingId}...`);

      const response = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch current data for backup: ${response.statusText}`);
      }

      const currentData = await response.json();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const backupId = `${listingId}_${timestamp}`;

      // Extract only local-only fields for backup
      const localFields = this.FIELD_CLASSIFICATIONS.local_only;
      const backup = {
        backup_id: backupId,
        listing_id: listingId,
        timestamp: timestamp,
        data: {},
      };

      localFields.forEach((field) => {
        if (currentData.data && currentData.data[field] !== undefined) {
          backup.data[field] = currentData.data[field];
        }
      });

      logger.log(
        `✅ Backup created for ${listingId}: ${Object.keys(backup.data).length} local fields`
      );
      return backup;
    } catch (error) {
      console.error(`❌ Backup failed for ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Validate that local data wasn't lost during update
   */
  validateLocalDataIntegrity(beforeData, afterData) {
    const localFields = this.FIELD_CLASSIFICATIONS.local_only;
    const issues = [];

    for (const field of localFields) {
      const beforeValue = beforeData.data?.[field];
      const afterValue = afterData.data?.[field];

      // If we had local data before but it's gone now, that's a problem
      if (beforeValue && !afterValue) {
        issues.push(`Lost local field: ${field}`);
      }
    }

    if (issues.length > 0) {
      throw new Error(`Local data integrity validation failed: ${issues.join(', ')}`);
    }

    logger.log(`✅ Local data integrity validated - no data loss detected`);
    return true;
  }

  /**
   * Get OAuth token from localStorage
   */
  getAuthToken() {
    return localStorage.getItem('etsy_access_token');
  }

  /**
   * Check if we have a valid token
   */
  hasValidToken() {
    const token = this.getAuthToken();
    return token && token.length > 0;
  }

  /**
   * Fetch complete data for a single listing
   */
  async fetchCompleteListingData(listingId) {
    if (!this.hasValidToken()) {
      throw new Error('No valid OAuth token found. Please connect to Etsy first.');
    }

    try {
      logger.log(`🔄 Fetching complete data for listing ${listingId}...`);

      const response = await fetch(`${this.baseUrl}/etsy-complete-listing/${listingId}`, {
        headers: {
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch listing data: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();
      logger.log(`✅ Successfully fetched complete data for listing ${listingId}`);

      return data;
    } catch (error) {
      console.error(`❌ Error fetching listing ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Store complete listing data in database
   */
  async storeListingData(listingId, completeData) {
    try {
      logger.log(`💾 Storing complete data for listing ${listingId} in database...`);

      // Store the complete Etsy API structure
      const listingData = {
        etsy_listing_id: listingId,
        listing_data: completeData.listing,
        images_data: completeData.images,
        inventory_data: completeData.inventory,
        variation_images_data: completeData.variationImages, // Add variation images data
        extracted_variations: completeData.extractedVariations,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Store in legacy etsy_listing_data table for raw data preservation
      const response = await fetch(`${this.baseUrl}/local-db/etsy-listing-data/${listingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(listingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to store listing data: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      logger.log(`✅ Successfully stored listing ${listingId} in database`);

      // Also update the stickers table with additional fields using safe update
      await this.safeEtsyUpdate(listingId, completeData);

      return result;
    } catch (error) {
      console.error(`❌ Error storing listing ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Safe update method with data protection and real-time monitoring
   */
  async safeEtsyUpdate(listingId, etsyData) {
    logger.log(`🛡️ Starting safe update for listing ${listingId}...`);

    try {
      // 1. Pre-update validation and monitoring
      logger.log(`🔍 Pre-update data completeness check...`);
      const preUpdateCompleteness = await dataProtectionService.validateDataCompleteness(listingId);
      logger.log(`📊 Pre-update status: ${preUpdateCompleteness.overall_status}`);

      // 2. Create comprehensive backup
      const backup = await dataProtectionService.createBackup(listingId, 'pre_etsy_update');

      // 3. Get current data for validation
      const currentResponse = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      const currentData = currentResponse.ok ? await currentResponse.json() : { data: {} };

      // 4. Validate source data completeness
      if (!etsyData.listing || !etsyData.inventory) {
        throw new Error('Incomplete Etsy source data - missing listing or inventory information');
      }

      // 5. Validate extracted variations completeness
      if (
        etsyData.extractedVariations &&
        etsyData.extractedVariations.completeness &&
        !etsyData.extractedVariations.completeness.is_complete
      ) {
        logger.warn(
          `⚠️ Variation extraction issues detected:`,
          etsyData.extractedVariations.completeness.issues
        );
      }

      // 6. Prepare safe update data (only Etsy fields)
      const safeUpdateData = {
        etsyListingId: listingId,
        title: etsyData.listing?.title || '',
        description: etsyData.listing?.description || '',
        etsyUrl: etsyData.listing?.url || '',
        etsyShareUrl: `https://clothedeye.etsy.com/listing/${listingId}`,
      };

      // Extract dimensions if available (this is computed data, not local)
      const dimensions = this.extractDimensionsFromListing(etsyData);
      if (dimensions && Object.keys(dimensions).length > 0) {
        safeUpdateData.dimensions = dimensions;
      }

      // Handle category as manual_override field - only update if current is null
      const shopSectionId = etsyData.listing?.shop_section_id;
      const categoryFromSection = shopSectionId
        ? this.determineCategoryFromShopSection(shopSectionId)
        : null;
      if (categoryFromSection && !currentData.data?.category) {
        safeUpdateData.category = categoryFromSection;
      }

      // 7. Filter to ensure only safe fields are sent
      const filteredData = this.filterSafeFields(safeUpdateData, 'etsy_only');
      // Add manual override fields
      if (safeUpdateData.category) {
        filteredData.category = safeUpdateData.category;
      }
      if (safeUpdateData.dimensions) {
        filteredData.dimensions = safeUpdateData.dimensions;
      }

      logger.log(`🔒 Updating with safe fields only: ${Object.keys(filteredData).join(', ')}`);

      // 8. Perform the update
      const response = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filteredData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Update failed: ${errorData.error || response.statusText}`);
      }

      // 9. Post-update validation and integrity check
      const updatedResponse = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`);
      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();

        // Validate local data wasn't lost
        this.validateLocalDataIntegrity(backup, updatedData);

        // Run comprehensive post-update validation
        const validation = await dataProtectionService.validateDataIntegrity(listingId, backup);
        if (!validation.success) {
          console.error(`❌ Post-update validation failed:`, validation.issues);
          // Consider rollback here if validation fails
        }
      }

      // 10. Real-time completeness monitoring
      logger.log(`🔍 Post-update data completeness check...`);
      const postUpdateCompleteness =
        await dataProtectionService.validateDataCompleteness(listingId);
      logger.log(`📊 Post-update status: ${postUpdateCompleteness.overall_status}`);

      // Log monitoring results
      if (postUpdateCompleteness.overall_status === 'COMPLETE') {
        logger.log(`✅ Data completeness monitoring: All checks passed`);
      } else {
        logger.warn(`⚠️ Data completeness monitoring: ${postUpdateCompleteness.overall_status}`);
      }

      logger.log(`✅ Safe update completed for listing ${listingId} with monitoring`);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error(`❌ Safe update failed for ${listingId}:`, error);

      // If we have a backup and the error is critical, consider auto-recovery
      logger.log(`🔄 Attempting to recover from backup due to critical error...`);
      try {
        await dataProtectionService.recoverFromBackup(listingId);
        logger.log(`✅ Emergency recovery completed for ${listingId}`);
      } catch (recoveryError) {
        console.error(`❌ Emergency recovery also failed:`, recoveryError);
      }

      throw error;
    }
  }

  /**
   * Safely update all listings from fresh Etsy data
   * Fetches current data from Etsy and uses safe update to preserve local data
   */
  async safeUpdateAllListingsFromEtsy() {
    try {
      logger.log(`🚀 Starting safe bulk update from Etsy...`);

      // Get all listings from Etsy
      const shopData = await this.fetchAllShopListings();
      const listings = shopData.results || [];

      logger.log(`📋 Found ${listings.length} listings to safely update`);

      loadingService.show({
        total: listings.length,
        current: 0,
        message: 'Starting safe update from Etsy...',
      });

      const results = {
        total: listings.length,
        successful: 0,
        failed: 0,
        errors: [],
        updates: [],
      };

      for (let i = 0; i < listings.length; i++) {
        const listing = listings[i];
        const listingId = listing.listing_id;

        try {
          loadingService.updateProgress({
            current: i + 1,
            total: listings.length,
            message: `Updating ${i + 1}/${listings.length}: ${listing.title}`,
          });

          logger.log(
            `🔄 ${i + 1}/${listings.length}: Safely updating listing ${listingId} - ${listing.title}`
          );

          // Fetch complete fresh data from Etsy
          const completeData = await this.fetchCompleteListingData(listingId);

          // Use safe update to only update Etsy fields, preserve local data
          await this.safeEtsyUpdate(listingId, completeData);

          results.successful++;
          results.updates.push({
            listingId,
            title: listing.title,
            status: 'success',
          });

          // Small delay to avoid rate limiting
          await new Promise((resolve) => setTimeout(resolve, TIMING.API_RETRY_SHORT));
        } catch (error) {
          console.error(`❌ Failed to safely update listing ${listingId}:`, error);
          results.failed++;
          results.errors.push({
            listingId,
            title: listing.title,
            error: error.message,
          });
        }
      }

      loadingService.updateProgress({
        current: listings.length,
        total: listings.length,
        message: `Completed: ${results.successful} successful, ${results.failed} failed`,
      });

      logger.log(`✅ Safe bulk update completed:`);
      logger.log(`   ✅ Successfully updated: ${results.successful} listings`);
      logger.log(`   ❌ Failed to update: ${results.failed} listings`);

      return results;
    } catch (error) {
      console.error(`❌ Safe bulk update failed:`, error);
      throw error;
    }
  }

  /**
   * Update stickers table with Etsy data and additional fields (DEPRECATED - use safeEtsyUpdate)
   * @deprecated Use safeEtsyUpdate instead for data protection
   */
  async updateStickerWithEtsyData(listingId, completeData) {
    try {
      logger.log(`📝 Updating sticker record for listing ${listingId}...`);

      // Extract dimensions from the listing (description takes priority)
      const dimensions = this.extractDimensionsFromListing(completeData);

      // Create the "share and save" URL format
      const etsyShareUrl = `https://clothedeye.etsy.com/listing/${listingId}`;

      // Get category from shop section (the correct way!)
      const shopSectionId = completeData.listing?.shop_section_id;
      const categoryFromSection = shopSectionId
        ? this.determineCategoryFromShopSection(shopSectionId)
        : null;

      // Extract all image URLs (keep full-size for modal)
      const allImageUrls = completeData.images?.results?.map((img) => img.url_fullxfull) || [];

      // Extract precomputed thumbnail URLs for cards
      const firstImageUrl = completeData.images?.results?.[0]?.url_170x135 || '';
      const largestImageUrl =
        completeData.images?.results?.[completeData.images.results.length - 1]?.url_170x135 ||
        firstImageUrl;

      // Extract available sizes and colors from extracted variations
      const availableSizes = completeData.extractedVariations?.sizes || [];
      const availableColors = completeData.extractedVariations?.colors || [];

      const stickerData = {
        etsyListingId: listingId,
        title: completeData.listing?.title || '',
        description: completeData.listing?.description || '',
        etsyUrl: completeData.listing?.url || '',
        etsyShareUrl: etsyShareUrl,
        state: completeData.listing?.state || 'active',
        quantity: completeData.listing?.quantity || 0,
        views: completeData.listing?.views || 0,
        num_favorers: completeData.listing?.num_favorers || 0,
        dimensions: dimensions,
        // Images
        first_image_url: firstImageUrl, // Small thumbnail for cards
        largest_size_image_url: largestImageUrl, // Small thumbnail for cards
        all_image_urls: allImageUrls, // Full-size for modal
        // Inventory and variations
        inventory_matrix: completeData.inventory || {},
        available_sizes: availableSizes,
        available_colors: availableColors,
        // 🚨 CRITICAL: Include variation images mapping
        variation_images_data: completeData.variationImages || {},
        sizeVariations: completeData.extractedVariations?.sizeVariations || [],
        colorVariations: completeData.extractedVariations?.colorVariations || [],
        // IMPORTANT: svgPath, sizes, publicVisible, isSet, displayOrder are NEVER sent
        // These fields are preserved as manually entered local data
      };

      // Only set category if we have a valid shop section mapping
      if (categoryFromSection) {
        stickerData.category = categoryFromSection;
      }

      // Update or create the unified sticker record
      const response = await fetch(`${this.baseUrl}/local-db/unified-stickers/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stickerData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.warn(
          `⚠️ Warning: Failed to update sticker record: ${errorData.error || response.statusText}`
        );
      } else {
        const result = await response.json();
        logger.log(`✅ Successfully updated sticker record for listing ${listingId}`);
        logger.log(`   📸 Images: ${allImageUrls.length}`);
        logger.log(`   📏 Sizes: ${availableSizes.length}`);
        logger.log(`   🎨 Colors: ${availableColors.length}`);
        if (result.data?.message) {
          logger.log(`   📋 ${result.data.message}`);
        }
      }
    } catch (error) {
      console.error(`❌ Error updating sticker record for ${listingId}:`, error);
    }
  }

  /**
   * Extract dimensions from listing data (description takes priority)
   */
  extractDimensionsFromListing(completeData) {
    return dimensionExtractor.extractAllDimensions(completeData);
  }

  /**
   * Determine category from Etsy shop section ID (the correct way!)
   */
  determineCategoryFromShopSection(shopSectionId) {
    // Official Etsy shop section mappings
    const sectionMapping = {
      38128096: 'Space Stickers',
      29962295: 'Cryptids',
      29962297: 'Vehicles Pop Culture',
      30012476: 'Pop Culture Stickers',
      31204616: 'Animal Stickers',
      31207552: 'Vehicle Stickers',
      31522296: 'Dinosaur Stickers', // This is where Compy should go!
      31522990: 'Outdoor Stickers',
      32569440: 'Police/Military/Fire',
      33728808: 'Horror',
      42035664: 'Offroad Slogans',
      42262700: 'SVGs',
    };

    const category = sectionMapping[shopSectionId];
    if (category) {
      logger.log(`🎯 Mapped shop section ${shopSectionId} to category: ${category}`);
      return category;
    } else {
      logger.log(`⚠️ Unknown shop section ID: ${shopSectionId}`);
      return null; // Don't overwrite existing category if we don't know the section
    }
  }

  /**
   * Fetch and store complete data for a listing
   */
  async fetchAndStoreListing(listingId) {
    try {
      logger.log(`🚀 Starting fetch and store process for listing ${listingId}...`);

      // Step 1: Fetch complete data from Etsy
      const completeData = await this.fetchCompleteListingData(listingId);

      // Note: Variation IDs are scraped separately via EtsyCrawler project
      // See: docs/ETSY_VARIATION_IDS.md for instructions

      // Step 2: Store raw data in etsy_listing_data table
      const result = await this.storeListingData(listingId, completeData);

      // Step 3: Process and update stickers_v2 table
      logger.log(`📝 Processing data into stickers_v2 table for listing ${listingId}...`);
      await this.updateStickerWithEtsyData(listingId, completeData);

      logger.log(`✅ Successfully completed fetch, store, and process for listing ${listingId}`);

      return {
        success: true,
        listingId,
        data: completeData,
        databaseResult: result,
      };
    } catch (error) {
      console.error(`❌ Failed to fetch and store listing ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Retry variation scraping for a listing (without re-fetching Etsy data)
   * @param {string} listingId - The Etsy listing ID
   * @param {string} listingUrl - The Etsy listing URL
   * @returns {Promise<Object>} Scraping result
   */
  async retryVariationScraping(listingId, listingUrl) {
    try {
      logger.log(`🔄 Retrying variation scraping for listing ${listingId}...`);

      const scrapeResponse = await fetch(`${this.baseUrl}/scrape-etsy-variations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listingUrl }),
      });

      const scrapeResult = await scrapeResponse.json();

      const updateData = {
        variation_scraping_last_attempt: new Date().toISOString(),
      };

      if (scrapeResult.success) {
        updateData.variation_scraping_status = 'success';
        updateData.variation_scraping_error = null;
        updateData.sizeVariations = JSON.stringify(scrapeResult.sizeVariations || []);
        updateData.colorVariations = JSON.stringify(scrapeResult.colorVariations || []);

        logger.log(
          `✅ Retry successful: ${scrapeResult.sizeVariations?.length || 0} sizes, ${scrapeResult.colorVariations?.length || 0} colors`
        );
      } else {
        updateData.variation_scraping_status = scrapeResult.isTimeout ? 'timeout' : 'failed';
        updateData.variation_scraping_error = scrapeResult.error || 'Failed to scrape variations';

        logger.warn(
          `⚠️ Retry ${updateData.variation_scraping_status}: ${updateData.variation_scraping_error}`
        );
      }

      // Update stickers_v2 with new scraping status
      const updateResponse = await fetch(`${this.baseUrl}/unified-stickers/${listingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update listing with scraping results');
      }

      return {
        success: scrapeResult.success,
        listingId,
        status: updateData.variation_scraping_status,
        sizeVariations: scrapeResult.sizeVariations || [],
        colorVariations: scrapeResult.colorVariations || [],
        error: updateData.variation_scraping_error,
      };
    } catch (error) {
      console.error(`❌ Failed to retry variation scraping for ${listingId}:`, error);

      // Update status to failed
      try {
        await fetch(`${this.baseUrl}/unified-stickers/${listingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            variation_scraping_status: 'failed',
            variation_scraping_error: error.message,
            variation_scraping_last_attempt: new Date().toISOString(),
          }),
        });
      } catch (updateError) {
        console.error('Failed to update error status:', updateError);
      }

      throw error;
    }
  }

  /**
   * Fetch all shop listings
   */
  async fetchAllShopListings() {
    if (!this.hasValidToken()) {
      throw new Error('No valid OAuth token found. Please connect to Etsy first.');
    }

    try {
      logger.log(`🔄 Fetching all listings for your shop...`);

      const response = await fetch(`${this.baseUrl}/etsy-shop-listings`, {
        headers: {
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch shop listings: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();
      logger.log(`✅ Successfully fetched ${data.results?.length || 0} listings for your shop`);

      return data;
    } catch (error) {
      console.error(`❌ Error fetching shop listings:`, error);
      throw error;
    }
  }

  /**
   * Fetch and store all shop listings
   */
  async fetchAndStoreAllListings() {
    try {
      logger.log(`🚀 Starting fetch and store process for all listings in your shop...`);

      // Step 1: Get all listings
      const shopData = await this.fetchAllShopListings();
      const listings = shopData.results || [];

      logger.log(`📋 Found ${listings.length} listings to process`);

      // Step 2: Process each listing
      const results = [];
      const errors = [];

      for (let i = 0; i < listings.length; i++) {
        const listing = listings[i];
        const listingId = listing.listing_id;

        try {
          loadingService.updateProgress({
            current: i + 1,
            total: listings.length,
            message: `Processing listing ${i + 1}/${listings.length}: ${listing.title}`,
          });

          logger.log(
            `📦 Processing listing ${i + 1}/${listings.length}: ${listingId} - ${listing.title}`
          );

          const result = await this.fetchAndStoreListing(listingId);
          results.push(result);

          // Add a small delay to avoid rate limiting
          await new Promise((resolve) => setTimeout(resolve, TIMING.API_RETRY_SHORT));
        } catch (error) {
          console.error(`❌ Failed to process listing ${listingId}:`, error);
          errors.push({
            listingId,
            error: error.message,
          });
        }
      }

      loadingService.updateProgress({
        current: listings.length,
        total: listings.length,
        message: `Completed: ${results.length} successful, ${errors.length} failed`,
      });

      logger.log(`✅ Completed fetch and store process:`);
      logger.log(`   ✅ Successfully processed: ${results.length} listings`);
      logger.log(`   ❌ Failed to process: ${errors.length} listings`);

      // Step 3: Automatically run precompute to extract image URLs
      logger.log(`🔄 Running automatic precompute to extract image URLs...`);
      try {
        await this.runPrecomputeListings();
        logger.log(`✅ Precompute completed successfully - images should now be visible`);
      } catch (precomputeError) {
        console.error(`⚠️ Warning: Precompute failed:`, precomputeError);
        // Don't fail the entire operation, just warn
      }

      return {
        success: true,
        totalListings: listings.length,
        successful: results.length,
        failed: errors.length,
        results,
        errors,
      };
    } catch (error) {
      console.error(`❌ Failed to fetch and store all listings:`, error);
      throw error;
    }
  }

  /**
   * Run precompute listings process via API call
   */
  async runPrecomputeListings() {
    try {
      logger.log(`🔄 Calling precompute-listings API endpoint...`);

      const response = await fetch(`${this.baseUrl}/precompute-listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Precompute API failed: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      logger.log(`✅ Precompute API completed:`, result);
      return result;
    } catch (error) {
      console.error(`❌ Error calling precompute API:`, error);
      throw error;
    }
  }

  /**
   * Get stored listing data from database
   */
  async getStoredListingData(listingId) {
    try {
      // Get raw Etsy data (keep this for backwards compatibility)
      const response = await fetch(`${this.baseUrl}/local-db/etsy-listing-data/${listingId}`);

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No data found
        }
        const errorData = await response.json();
        throw new Error(
          `Failed to get stored listing data: ${errorData.error || response.statusText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`❌ Error getting stored listing data for ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Check if listing data exists in database
   */
  async hasStoredListingData(listingId) {
    const data = await this.getStoredListingData(listingId);
    return data !== null;
  }
}

export default new EtsyDataService();
